/**
 * Viewport Animation System
 * Triggers animations when elements enter viewport using Intersection Observer.
 *
 * Two markup hooks share a single observer:
 *   [data-animate]  — adds .is-animated on reveal (CSS transitions in animations.css)
 *   [data-countup]  — ticks the element's number from 0 to its rendered value on reveal
 *
 * @package Felix
 */

const COUNTUP_DEFAULT_MS = 1400

// Parsed count-up state keyed by element (see primeCountUp).
const countUpState = new WeakMap()

function prefersReducedMotion() {
	return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Parse a number out of an element's text, preserving prefix/suffix and format.
 * Handles thousands separators and decimals, e.g. "$1,200", "80%", "3.5x".
 * @param {HTMLElement} el
 * @return {?{prefix:string,suffix:string,target:number,decimals:number,grouping:boolean}}
 */
function parseCountUp(el) {
	const match = (el.textContent || '').trim().match(/^(\D*?)(-?[\d,]*\.?\d+)(.*)$/)
	if (!match) {
		return null
	}
	const [, prefix, numStr, suffix] = match
	const target = parseFloat(numStr.replace(/,/g, ''))
	if (!isFinite(target)) {
		return null
	}
	return {
		prefix,
		suffix,
		target,
		decimals: numStr.includes('.') ? numStr.split('.')[1].length : 0,
		grouping: numStr.includes(','),
	}
}

function formatCountUp(value, state) {
	const display = state.grouping
		? value.toLocaleString('en-US', {
				minimumFractionDigits: state.decimals,
				maximumFractionDigits: state.decimals,
		  })
		: value.toFixed(state.decimals)
	return `${state.prefix}${display}${state.suffix}`
}

/**
 * Store the parsed target and reset the element to zero so it can tick up.
 * @param {HTMLElement} el
 * @return {boolean} True when the element held a countable number.
 */
function primeCountUp(el) {
	const state = parseCountUp(el)
	if (!state) {
		return false
	}
	countUpState.set(el, state)
	el.textContent = formatCountUp(0, state)
	return true
}

/**
 * Animate a primed element from 0 to its target value.
 * Honours data-countup-duration and data-countup-delay.
 * @param {HTMLElement} el
 */
function runCountUp(el) {
	const state = countUpState.get(el)
	if (!state) {
		return
	}
	const duration = parseInt(el.dataset.countupDuration, 10) || COUNTUP_DEFAULT_MS
	const delay = parseInt(el.dataset.countupDelay, 10) || 0
	const ease = (t) => 1 - Math.pow(1 - t, 3)

	const start = () => {
		const startTime = performance.now()
		const step = (now) => {
			const progress = Math.min((now - startTime) / duration, 1)
			el.textContent = formatCountUp(state.target * ease(progress), state)
			if (progress < 1) {
				requestAnimationFrame(step)
			}
		}
		requestAnimationFrame(step)
	}

	if (delay > 0) {
		setTimeout(start, delay)
	} else {
		start()
	}
}

/**
 * Initialize viewport animations
 * Observes [data-animate] and [data-countup] elements and reveals them on scroll.
 */
export function initAnimations() {
	// Only run on frontend (not in block editor)
	if (document.body.classList.contains('block-editor-page')) {
		return
	}

	const reduceMotion = prefersReducedMotion()

	// Intersection Observer options
	const options = {
		root: null, // Use viewport as root
		rootMargin: '0px 0px -100px 0px', // Trigger 100px before entering viewport
		threshold: 0.1, // 10% of element must be visible
	}

	// Create observer — drives both the class toggle and the count-up ticker.
	const observer = new IntersectionObserver((entries) => {
		entries.forEach((entry) => {
			if (!entry.isIntersecting) {
				return
			}
			const el = entry.target

			// Add animated class to trigger CSS transition
			if (el.hasAttribute('data-animate')) {
				el.classList.add('is-animated')
			}

			// Tick primed numbers up to their target
			if (countUpState.has(el)) {
				runCountUp(el)
			}

			// Unobserve after animating (performance optimization)
			observer.unobserve(el)
		})
	}, options)

	// Observe all elements with data-animate attribute
	const animatedElements = document.querySelectorAll('[data-animate]')
	animatedElements.forEach((el) => {
		// Split text into words for word-by-word animation
		if (el.dataset.animate === 'slide-words') {
			splitTextIntoWords(el)
		}
		observer.observe(el)
	})

	// Prime and observe count-up numbers. Under reduced motion we leave the
	// server-rendered final value untouched (no reset, no ticker).
	if (!reduceMotion) {
		document.querySelectorAll('[data-countup]').forEach((el) => {
			if (primeCountUp(el)) {
				observer.observe(el)
			}
		})
	}

	// Handle staggered children
	const staggerContainers = document.querySelectorAll('[data-animate-stagger]')
	staggerContainers.forEach((parent) => {
		const staggerDelay = parseInt(parent.dataset.animateStagger) || 100
		const children = parent.querySelectorAll('[data-animate-child]')

		children.forEach((child, index) => {
			// Set progressive delay for each child
			child.style.transitionDelay = `${index * staggerDelay}ms`
		})
	})

	initStickyCTAObserver()
}

/**
 * Split text into individual words wrapped in spans
 * Each word gets animated with progressive delay
 * @param {HTMLElement} element - Element to split
 */
function splitTextIntoWords(element) {
	const text = element.textContent.trim()
	const words = text.split(/\s+/)
	const wordDelay = parseInt(element.dataset.wordDelay) || 50 // ms delay between words

	// Clear original text
	element.textContent = ''

	// Wrap each word in a span
	words.forEach((word, index) => {
		const span = document.createElement('span')
		span.className = 'word'
		span.textContent = word
		span.style.transitionDelay = `${index * wordDelay}ms`
		element.appendChild(span)

		// Add space after word (except last word)
		if (index < words.length - 1) {
			element.appendChild(document.createTextNode(' '))
		}
	})
}

/**
 * Toggle sticky CTA visibility after the first content block.
 *
 * Uses IntersectionObserver so we do not depend on scroll listeners.
 */
function initStickyCTAObserver() {
	const stickyCta = document.querySelector('[data-felix-sticky-cta]')
	if (!stickyCta || stickyCta.dataset.felixStickyCtaInit === 'true') {
		return
	}

	stickyCta.dataset.felixStickyCtaInit = 'true'

	const firstBlock =
		document.querySelector('main .wp-block-post-content > *:first-child') ||
		document.querySelector('main .entry-content > *:first-child') ||
		document.querySelector('main > *:first-child')

	if (!firstBlock) {
		return
	}

	const setVisible = (isVisible) => {
		stickyCta.classList.toggle('is-visible', isVisible)
	}

	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				// Show CTA when roughly 70% of the first block has scrolled away.
				const visibleRatio = entry.intersectionRatio
				setVisible(visibleRatio <= 0.3)
			})
		},
		{
			root: null,
			threshold: [0, 0.3, 1],
		}
	)

	observer.observe(firstBlock)
}

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initAnimations)
} else {
	initAnimations()
}
